import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Zap, ArrowRight, Trophy, Gift, Users } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div className="relative hidden md:flex flex-col justify-between p-12 bg-brand-ink text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #FF5C00 0%, transparent 40%), radial-gradient(circle at 80% 70%, #0047FF 0%, transparent 45%)"
        }} />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-orange border-2 border-white rounded-xl flex items-center justify-center shadow-[4px_4px_0_0_#FFF]">
            <Zap className="w-6 h-6" strokeWidth={3} />
          </div>
          <div>
            <div className="font-display font-black text-2xl leading-none">ReferPro</div>
            <div className="text-xs uppercase tracking-widest text-white/70">Refer · Earn · Recognise</div>
          </div>
        </div>

        <div className="relative">
          <div className="font-display font-black text-5xl leading-[1.02] tracking-tight">
            Great hires<br/>
            come from<br/>
            <span className="text-brand-yellow">great people.</span>
          </div>
          <p className="mt-6 text-white/80 text-lg max-w-md">
            Turn your team into your best recruitment channel. Earn points, climb the leaderboard, and redeem real rewards — 1 point = €1 to your payroll.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            <div className="border-2 border-white/50 rounded-2xl p-4">
              <Trophy className="w-6 h-6 text-brand-yellow mb-2" />
              <div className="font-display font-black text-xl">#1</div>
              <div className="text-[11px] uppercase tracking-widest text-white/70">Leaderboard</div>
            </div>
            <div className="border-2 border-white/50 rounded-2xl p-4">
              <Gift className="w-6 h-6 text-brand-green mb-2" />
              <div className="font-display font-black text-xl">€€€</div>
              <div className="text-[11px] uppercase tracking-widest text-white/70">Payroll bonus</div>
            </div>
            <div className="border-2 border-white/50 rounded-2xl p-4">
              <Users className="w-6 h-6 text-brand-orange mb-2" />
              <div className="font-display font-black text-xl">5x</div>
              <div className="text-[11px] uppercase tracking-widest text-white/70">Faster hires</div>
            </div>
          </div>
        </div>

        <div className="relative text-xs text-white/60">© ReferPro 2026</div>
      </div>

      {/* Right panel - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
            <div className="font-display font-black text-xl">ReferPro</div>
          </div>

          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Sign in</h1>
          <p className="text-neutral-600 mt-2">Welcome back. Let's find some great talent.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-brutal"
                autoComplete="email"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-brutal"
                autoComplete="current-password"
                required
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-brutal btn-primary w-full py-3 text-base"
              data-testid="login-submit"
            >
              {busy ? "Signing in..." : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs font-bold underline text-neutral-700 hover:text-black"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
